import React, { useState, useEffect } from 'react';
import { QuoteState, LineItem, Contact, Country } from '../types';
import { Plus, Trash2, ChevronDown, ChevronUp, Receipt, User, StickyNote, Truck, CreditCard, Bookmark, List, X, Cloud, Search, Building2, MapPin, Hash, Lock, CheckCircle2, Globe, AlertCircle } from 'lucide-react';

const PRODUCT_CATALOG = [
  "Bitumen 60/70",
  "Bitumen 80/100",
  "Bitumen 40/50",
  "Bitumen MC30",
  "Bitumen K1-60",
  "Bitumen PG 70-10",
  "Bitumen PG 70-22",
  "Bitumen PG 70-16",
  "Bitumen PG 76-16",
  "Bitumen PG 76-10"
];

const UNIT_OPTIONS = [
  "M.T",
  "Kg",
  "Liters",
  "Drums",
  "Units",
  "Sets"
];

const INCOTERM_OPTIONS = [
  "Ex-Warehouse",
  "C&F Dar-es-Salaam",
  "C&F Mombasa",
  "C&F Tanga",
  "C&F Zanzibar",
  "D.D.U Nkhotakota,Malawi"
];

const CURRENCY_MAPPING: Record<Country, string[]> = {
  [Country.TANZANIA]: ['USD', 'TZS'],
  [Country.UGANDA]: ['USD', 'UGX'],
  [Country.KENYA]: ['USD', 'KES']
};

interface QuoteEditorProps {
  data: QuoteState;
  activeCountry: Country;
  onChange: (newData: QuoteState) => void;
  onConfirm: () => void;
  crmContacts: Contact[];
}

type Section = 'client' | 'details' | 'items' | 'notes';

interface FormattedNumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number;
  onChange: (value: number) => void;
  format?: 'currency' | 'decimal';
}

const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({ 
  value, 
  onChange, 
  format = 'decimal',
  className,
  onFocus,
  onBlur,
  ...props 
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.readOnly) return;
    setIsFocused(true);
    setInputValue(value === 0 ? '' : value.toString());
    if (onFocus) onFocus(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    if (rawValue !== '' && !/^\d*\.?\d*$/.test(rawValue)) {
        return;
    }
    setInputValue(rawValue);
    if (rawValue === '' || rawValue === '.') {
        onChange(0);
        return;
    }
    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed)) {
        onChange(parsed);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const displayValue = isFocused 
    ? inputValue 
    : (format === 'currency' ? (value || 0).toFixed(2) : (value || 0).toString());

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    />
  );
};

export const QuoteEditor: React.FC<QuoteEditorProps> = ({ data, activeCountry, onChange, onConfirm, crmContacts }) => {
  const [activeSection, setActiveSection] = useState<Section>('items');
  const [savedTemplates, setSavedTemplates] = useState<Omit<LineItem, 'id'>[]>([]);
  const [showTemplates, setShowTemplates] = useState(false);
  
  // CRM Search State
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchTerm] = useState('');

  useEffect(() => {
    const loadedTemplates = localStorage.getItem('smartQuote_itemTemplates');
    if (loadedTemplates) {
        try {
            setSavedTemplates(JSON.parse(loadedTemplates));
        } catch (e) {
            console.error('Failed to load templates', e);
        }
    }
  }, []);

  const updateField = (section: keyof QuoteState, field: string, value: any) => {
    const newData = {
      ...data,
      [section]: {
        ...data[section],
        [field]: value,
      },
    };
    onChange(newData);
  };

  const handleIncotermChange = (value: string) => {
    let deliveryTerms = data.details.deliveryTerms;
    let taxRate = data.details.taxRate;

    // Logic: C&F and D.D.U terms are VAT Exempt / Not Applicable
    if (value.startsWith('C&F') || value.startsWith('D.D.U')) {
        taxRate = 0;
    } else {
        // Restore default if switching back to Ex-Warehouse
        taxRate = activeCountry === Country.KENYA ? 16 : 18;
    }
    
    if (value !== 'Ex-Warehouse') {
        deliveryTerms = `Goods shall be produced within 50 days and shipped on first available vessel to ${activeCountry}`;
    } else {
        deliveryTerms = "Buyer should arrange loading of goods from Seller's warehouse after payment in received in seller's bank account";
    }

    const newData = {
      ...data,
      details: {
        ...data.details,
        incoterms: value,
        deliveryTerms: deliveryTerms,
        taxRate: taxRate
      },
    };
    onChange(newData);
  };

  const updateItem = (id: string, field: keyof LineItem, value: any) => {
    const newItems = data.items.map((item) =>
      item.id === id ? { ...item, [field]: value } : item
    );
    onChange({ ...data, items: newItems });
  };

  const addItem = () => {
    const newItem: LineItem = {
      id: crypto.randomUUID(),
      name: '',
      description: '(Packed in steel drums)',
      quantity: 1,
      price: 0,
      unit: 'Drums',
      drumWeight: 190
    };
    onChange({ ...data, items: [...data.items, newItem] });
  };

  const removeItem = (id: string) => {
    onChange({ ...data, items: data.items.filter((item) => item.id !== id) });
  };

  const handleImportFromCRM = (contact: Contact) => {
      onChange({
          ...data,
          client: {
              name: contact.companyName,
              email: contact.email,
              phone: contact.phone,
              address: contact.companyAddress,
              tin: contact.tin,
              vrn: contact.vrn
          }
      });
      setIsSearchOpen(false);
      setSearchTerm('');
  };

  const handleSaveTemplate = (item: LineItem) => {
    if (!item.name.trim()) {
        alert("Please enter an item name before saving as a template.");
        return;
    }
    const template: Omit<LineItem, 'id'> = {
        name: item.name,
        description: item.description,
        quantity: 1,
        price: item.price,
        unit: item.unit,
        drumWeight: item.drumWeight
    };
    const exists = savedTemplates.some(t => t.name === template.name);
    let newTemplates;
    if (exists) {
        if (!window.confirm(`Template "${template.name}" already exists. Overwrite?`)) return;
        newTemplates = savedTemplates.map(t => t.name === template.name ? template : t);
    } else {
        newTemplates = [...savedTemplates, template];
    }
    setSavedTemplates(newTemplates);
    localStorage.setItem('smartQuote_itemTemplates', JSON.stringify(newTemplates));
  };

  const handleInsertTemplate = (template: Omit<LineItem, 'id'>) => {
    const newItem: LineItem = { ...template, id: crypto.randomUUID(), quantity: 1 };
    onChange({ ...data, items: [...data.items, newItem] });
    setShowTemplates(false);
  };

  const handleDeleteTemplate = (name: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if(window.confirm(`Delete template "${name}"?`)) {
          const newTemplates = savedTemplates.filter(t => t.name !== name);
          setSavedTemplates(newTemplates);
          localStorage.setItem('smartQuote_itemTemplates', JSON.stringify(newTemplates));
      }
  };

  const SectionHeader = ({ id, title, icon: Icon }: { id: Section; title: string; icon: any }) => (
    <button
      onClick={() => setActiveSection(activeSection === id ? 'items' : id)}
      className={`w-full flex items-center justify-between p-4 bg-white border-b border-slate-100 hover:bg-slate-50 transition-colors ${activeSection === id ? 'text-indigo-600' : 'text-slate-700'}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <span className="font-semibold">{title}</span>
      </div>
      {activeSection === id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
    </button>
  );

  const filteredCrmContacts = crmContacts.filter(c => 
    c.companyName.toLowerCase().includes(searchQuery.toLowerCase()) || 
    c.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.tin && c.tin.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const canConfirm = data.items.length > 0 && data.client.name.trim().length > 0;

  const currentCurrencies = CURRENCY_MAPPING[activeCountry] || ['USD'];
  const taxIdLabel = activeCountry === Country.KENYA ? 'PIN' : 'TIN';
  const isKenya = activeCountry === Country.KENYA;
  const isVatNA = data.details.incoterms.startsWith('C&F');

  return (
    <div className="h-full flex flex-col bg-white border-r border-slate-200 shadow-sm overflow-hidden relative">
        <div className="flex-1 overflow-y-auto">
            {/* Buyer Info */}
            <div className="border-b border-slate-200">
                <SectionHeader id="client" title="Buyer Management" icon={User} />
                {activeSection === 'client' && (
                <div className="p-4 space-y-3 bg-slate-50 animate-in slide-in-from-top-2 duration-200">
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-1.5 ml-1">
                            <label className="block text-[10px] font-black text-indigo-500 uppercase tracking-widest">Load Buyer from CRM</label>
                            <button 
                                onClick={() => setIsSearchOpen(true)}
                                className="text-[10px] font-black text-indigo-600 uppercase tracking-widest hover:underline flex items-center gap-1"
                            >
                                <Search size={10} /> Search Database
                            </button>
                        </div>
                        <div className="relative">
                            <select 
                                className="w-full p-2.5 border border-indigo-200 rounded-xl text-sm bg-white appearance-none pr-10 focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-slate-700 transition-all shadow-sm"
                                onChange={(e) => {
                                    const contact = crmContacts.find(c => c.id === e.target.value);
                                    if (contact) handleImportFromCRM(contact);
                                }}
                                value=""
                            >
                                <option value="" disabled>Select from Registry...</option>
                                {crmContacts.map((contact) => (
                                    <option key={contact.id} value={contact.id}>{contact.companyName}</option>
                                ))}
                            </select>
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-2">
                                <Cloud className="w-3.5 h-3.5 text-indigo-400" />
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-slate-200 pt-3">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Buyer Name</label>
                        <input
                        type="text"
                        placeholder="Company or Person Name"
                        className="w-full p-2 border rounded text-sm"
                        value={data.client.name}
                        onChange={(e) => updateField('client', 'name', e.target.value)}
                        />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                            <input
                            type="email"
                            placeholder="Email Address"
                            className="w-full p-2 border rounded text-sm"
                            value={data.client.email}
                            onChange={(e) => updateField('client', 'email', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                            <input
                            type="text"
                            placeholder="Phone Number"
                            className="w-full p-2 border rounded text-sm"
                            value={data.client.phone}
                            onChange={(e) => updateField('client', 'phone', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">{taxIdLabel}</label>
                            <input
                            type="text"
                            placeholder={`${taxIdLabel} Number`}
                            className="w-full p-2 border rounded text-sm"
                            value={data.client.tin || ''}
                            onChange={(e) => updateField('client', 'tin', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">VRN</label>
                            <input
                            type="text"
                            placeholder="VAT Reg Number"
                            className="w-full p-2 border rounded text-sm"
                            value={data.client.vrn || ''}
                            onChange={(e) => updateField('client', 'vrn', e.target.value)}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Buyer Address</label>
                        <textarea
                        placeholder="Client Address & Location"
                        rows={3}
                        className="w-full p-2 border rounded text-sm resize-none"
                        value={data.client.address}
                        onChange={(e) => updateField('client', 'address', e.target.value)}
                        />
                    </div>
                </div>
                )}
            </div>

            {/* Quote Details */}
            <div className="border-b border-slate-200">
                <SectionHeader id="details" title="Invoice Specifics" icon={Receipt} />
                {activeSection === 'details' && (
                <div className="p-4 grid grid-cols-2 gap-4 bg-slate-50 animate-in slide-in-from-top-2 duration-200">
                    <div className="col-span-2">
                        <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1.5">
                            Proforma No.
                            <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-[4px] text-[8px] font-black uppercase tracking-tighter">Auto-Managed</span>
                        </label>
                        <div className="relative group/num">
                            <input
                                type="text"
                                readOnly
                                className="w-full p-2.5 bg-white border border-indigo-200 rounded-xl text-sm font-black text-indigo-700 cursor-not-allowed shadow-sm pr-8"
                                value={data.details.number}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-400">
                                <Lock size={14} />
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Date</label>
                        <input
                        type="date"
                        className="w-full p-2 border rounded text-sm"
                        value={data.details.date}
                        onChange={(e) => updateField('details', 'date', e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Price Validity</label>
                        <input
                        type="date"
                        className="w-full p-2 border rounded text-sm"
                        value={data.details.priceValidity}
                        onChange={(e) => updateField('details', 'priceValidity', e.target.value)}
                        />
                    </div>
                     <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Currency</label>
                        <select
                        className="w-full p-2 border rounded text-sm bg-white font-bold"
                        value={data.details.currency}
                        onChange={(e) => updateField('details', 'currency', e.target.value)}
                        >
                            {currentCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                            VAT/Tax (%)
                            {isVatNA && <span className="text-[8px] font-black text-rose-500 uppercase">C&F Locked</span>}
                        </label>
                        <FormattedNumberInput
                            className={`w-full p-2 border rounded text-sm ${isVatNA ? 'bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200' : ''}`}
                            value={data.details.taxRate}
                            onChange={(val) => !isVatNA && updateField('details', 'taxRate', val)}
                            readOnly={isVatNA}
                        />
                    </div>
                    <div className="col-span-2">
                         <label className="block text-xs font-medium text-slate-500 mb-1">Amount in Words</label>
                        <textarea
                            rows={2}
                            className="w-full p-2 border rounded text-sm uppercase"
                            value={data.details.amountInWords}
                            onChange={(e) => updateField('details', 'amountInWords', e.target.value)}
                            placeholder="ONE THOUSAND ONLY..."
                        />
                    </div>
                </div>
                )}
            </div>

            {/* Terms */}
            <div className="border-b border-slate-200">
                <SectionHeader id="notes" title="Terms & Conditions" icon={StickyNote} />
                 {activeSection === 'notes' && (
                <div className="p-4 space-y-4 bg-slate-50 animate-in slide-in-from-top-2 duration-200">
                     <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                            <CreditCard className="w-3 h-3" /> Payment Terms
                        </label>
                        <textarea
                            rows={2}
                            className="w-full p-2 border rounded text-sm"
                            value={data.details.paymentTerms}
                            onChange={(e) => updateField('details', 'paymentTerms', e.target.value)}
                        />
                    </div>
                    {!isKenya && (
                        <div>
                             <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                                <Globe className="w-3 h-3" /> Incoterms
                            </label>
                            <select
                                className="w-full p-2 border rounded text-sm bg-white font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                                value={data.details.incoterms}
                                onChange={(e) => handleIncotermChange(e.target.value)}
                            >
                                {INCOTERM_OPTIONS.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            {isVatNA && (
                                <div className="mt-2 p-2 bg-indigo-50 rounded-lg flex items-start gap-2 border border-indigo-100">
                                    <AlertCircle size={14} className="text-indigo-600 mt-0.5" />
                                    <p className="text-[10px] font-bold text-indigo-700 leading-tight">Export Incoterms (C&F / D.D.U) are VAT Exempt (Not Applicable).</p>
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                         <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                            <Truck className="w-3 h-3" /> Delivery Terms
                        </label>
                        <div className="space-y-2">
                            <textarea
                                rows={2}
                                className="w-full p-2 border rounded text-sm"
                                value={data.details.deliveryTerms}
                                onChange={(e) => updateField('details', 'deliveryTerms', e.target.value)}
                                placeholder="Specify delivery conditions (e.g. Ex-Warehouse, C&F Dar-es-Salaam)..."
                            />
                        </div>
                    </div>
                    <div>
                         <label className="block text-xs font-medium text-slate-500 mb-1 flex items-center gap-1">
                            <StickyNote className="w-3 h-3" /> Notes / Footers
                         </label>
                        <textarea
                            rows={3}
                            className="w-full p-2 border rounded text-sm"
                            value={data.details.notes}
                            onChange={(e) => updateField('details', 'notes', e.target.value)}
                            placeholder="Additional notes..."
                        />
                    </div>
                </div>
                )}
            </div>
            
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                <SectionHeader id="items" title={`Line Items (${data.items.length})`} icon={Receipt} />
                {activeSection === 'items' && (
                  <div className="flex-1 overflow-y-auto p-4 bg-white animate-in slide-in-from-top-2 duration-200">
                    <div className="space-y-4">
                        {data.items.map((item, index) => (
                            <div key={item.id} className="p-4 rounded-xl border border-slate-200 bg-white shadow-sm group hover:border-indigo-300 transition-colors relative">
                                <div className="flex justify-between items-start mb-2">
                                    <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">#{index + 1}</span>
                                    <div className="flex items-center gap-1">
                                        <button 
                                            onClick={() => handleSaveTemplate(item)}
                                            className="text-slate-300 hover:text-amber-500 transition-colors p-1"
                                            title="Save as Template"
                                        >
                                            <Bookmark className="w-4 h-4" />
                                        </button>
                                        <button 
                                            onClick={() => removeItem(item.id)} 
                                            className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                            title="Delete Item"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <textarea
                                        placeholder="Product / Item Name"
                                        rows={1}
                                        className="w-full p-2 font-medium border-b border-slate-200 focus:border-indigo-500 focus:ring-0 outline-none transition-colors resize-y min-h-[40px]"
                                        value={item.name}
                                        onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                                    />
                                    <textarea
                                        placeholder="Description"
                                        rows={2}
                                        className="w-full p-2 text-sm text-slate-600 bg-slate-50 rounded focus:bg-white border border-transparent focus:border-indigo-200 outline-none transition-all resize-y"
                                        value={item.description}
                                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                    />
                                    <div className={`grid grid-cols-2 ${isKenya ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-2`}>
                                        <div>
                                            <label className="text-[9px] uppercase text-slate-400 font-black tracking-tight">Qty</label>
                                            <FormattedNumberInput
                                                className="w-full p-1.5 border rounded text-sm text-center"
                                                value={item.quantity}
                                                onChange={(val) => updateItem(item.id, 'quantity', val)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[9px] uppercase text-slate-400 font-black tracking-tight">Unit</label>
                                            <input
                                                type="text"
                                                list="unit-options"
                                                placeholder="e.g. Kg"
                                                className="w-full p-1.5 border rounded text-sm text-center"
                                                value={item.unit || ''}
                                                onChange={(e) => updateItem(item.id, 'unit', e.target.value)}
                                            />
                                        </div>
                                        {isKenya && (
                                            <div>
                                                <label className="text-[9px] uppercase text-slate-400 font-black tracking-tight">Drum Wt</label>
                                                <FormattedNumberInput
                                                    className="w-full p-1.5 border rounded text-sm text-center bg-indigo-50/30"
                                                    value={item.drumWeight || 0}
                                                    onChange={(val) => updateItem(item.id, 'drumWeight', val)}
                                                />
                                            </div>
                                        )}
                                        <div>
                                            <label className="text-[9px] uppercase text-slate-400 font-black tracking-tight">{isKenya ? 'Base Price' : 'Rate'}</label>
                                            <FormattedNumberInput
                                                className="w-full p-1.5 border rounded text-sm text-right font-black"
                                                value={item.price}
                                                onChange={(val) => updateItem(item.id, 'price', val)}
                                                format="currency"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 flex flex-col gap-3 pb-8">
                        <button
                            onClick={addItem}
                            className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 transition-all flex justify-center items-center gap-2 font-medium"
                        >
                            <Plus className="w-5 h-5" />
                            Add Line Item
                        </button>
                        <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <button
                                onClick={() => setShowTemplates(!showTemplates)}
                                className="w-full py-3 px-4 flex items-center justify-between text-slate-600 hover:bg-slate-100 transition-colors"
                            >
                                <div className="flex items-center gap-2 font-medium">
                                    <List className="w-4 h-4 text-indigo-500" />
                                    Saved Templates
                                    <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{savedTemplates.length}</span>
                                </div>
                                {showTemplates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                            {showTemplates && (
                                <div className="border-t border-slate-200 divide-y divide-slate-100 max-h-60 overflow-y-auto">
                                    {savedTemplates.length === 0 ? (
                                        <div className="p-4 text-center text-xs text-slate-400">
                                            No templates saved.
                                        </div>
                                    ) : (
                                        savedTemplates.map((template, idx) => (
                                            <div key={`${template.name}-${idx}`} className="p-3 hover:bg-white flex items-center justify-between group transition-colors">
                                                <div className="flex-1 cursor-pointer" onClick={() => handleInsertTemplate(template)}>
                                                    <p className="font-medium text-sm text-slate-800">{template.name}</p>
                                                    <p className="text-xs text-slate-500 truncate max-w-[200px]">
                                                        {template.price > 0 ? (
                                                            <span className="font-medium text-slate-700">{template.price.toLocaleString()} / {template.unit || 'unit'}</span>
                                                        ) : 'No price set'}
                                                        {template.description && ` • ${template.description}`}
                                                    </p>
                                                </div>
                                                <button 
                                                    onClick={(e) => handleDeleteTemplate(template.name, e)}
                                                    className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                                    title="Delete Template"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                  </div>
                )}
            </div>
        </div>
        <div className="p-4 bg-white border-t border-slate-200 z-10">
            <button
                onClick={onConfirm}
                disabled={!canConfirm}
                className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-[0.3em] transition-all flex items-center justify-center gap-3 shadow-xl active:scale-95 ${canConfirm ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-emerald-100' : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'}`}
            >
                <CheckCircle2 size={20} />
                Confirm Proforma
            </button>
        </div>
        <datalist id="product-catalog">
            {PRODUCT_CATALOG.map((product) => (
                <option key={product} value={product} />
            ))}
        </datalist>
        <datalist id="unit-options">
            {UNIT_OPTIONS.map((unit) => (
                <option key={unit} value={unit} />
            ))}
        </datalist>

        {/* CRM Search Modal Overlay */}
        {isSearchOpen && (
            <div className="absolute inset-0 z-50 bg-slate-900/10 backdrop-blur-sm flex flex-col animate-in fade-in duration-300">
                <div className="flex-1 flex flex-col bg-white overflow-hidden shadow-2xl">
                    <header className="p-4 bg-indigo-600 text-white flex justify-between items-center shadow-lg">
                        <div className="flex items-center gap-2">
                            <Search size={16} />
                            <h3 className="font-black text-xs uppercase tracking-widest">Find Buyer</h3>
                        </div>
                        <button onClick={() => setIsSearchOpen(false)} className="p-1 hover:bg-white/10 rounded transition-colors">
                            <X size={18} />
                        </button>
                    </header>

                    <div className="p-4 border-b border-slate-100 bg-slate-50">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input 
                                type="text" 
                                autoFocus
                                value={searchQuery}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, person or TIN..."
                                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:ring-4 focus:ring-indigo-500/10 outline-none font-medium transition-all"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar bg-slate-50/30">
                        {filteredCrmContacts.length > 0 ? (
                            filteredCrmContacts.map(contact => (
                                <button 
                                    key={contact.id}
                                    onClick={() => handleImportFromCRM(contact)}
                                    className="w-full text-left p-3 bg-white border border-slate-200 rounded-xl hover:border-indigo-400 hover:shadow-md transition-all group"
                                >
                                    <div className="flex items-start justify-between mb-1">
                                        <div className="font-black text-slate-800 text-xs uppercase tracking-tight truncate max-w-[80%]">{contact.companyName}</div>
                                        <Cloud size={12} className="text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400">
                                            <User size={10} className="text-slate-300" />
                                            {contact.contactPerson}
                                        </div>
                                        {contact.tin && (
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400">
                                                <Hash size={10} className="text-slate-300" />
                                                TIN: {contact.tin}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 truncate">
                                            <MapPin size={10} className="text-slate-300" />
                                            <span className="truncate">{contact.companyAddress}</span>
                                        </div>
                                    </div>
                                </button>
                            ))
                        ) : (
                            <div className="py-12 text-center flex flex-col items-center justify-center opacity-50">
                                <Building2 size={32} className="text-slate-200 mb-2" />
                                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">No buyers found</p>
                            </div>
                        )}
                    </div>
                    
                    <footer className="p-3 bg-white border-t border-slate-100 text-center">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter italic">Searching Master CRM Database</p>
                    </footer>
                </div>
            </div>
        )}
    </div>
  );
};